import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Link } from "react-router-dom";
import { Gavel, Crown, Users, ArrowRight, Lock } from "lucide-react";
import { useLang } from "@/LangContext";
import { Reveal, Chapter } from "@/components/Reveal";
import Countdown from "@/components/Countdown";
import { API, getToken } from "@/lib/auth";

export default function Auction() {
  const { t } = useLang();
  const a = t.auction;
  const { data } = useQuery({
    queryKey: ["auction-public"],
    queryFn: async () => (await axios.get(`${API}/auction/public`)).data,
    refetchInterval: 30_000,
  });
  const endsAt = data?.ends_at || "2026-12-31T22:00:00+00:00";
  const isOpen = data ? data.open : true;

  return (
    <section id="subasta" data-testid="auction-section" className="relative overflow-hidden py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Chapter number={a.chapter} label={a.label} />
        </Reveal>
        <div className="mt-8 grid gap-12 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <Reveal delay={0.1}>
              <h2 className="max-w-3xl font-display text-3xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
                {a.title}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">{a.subtitle}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10">
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{a.closesLabel}</span>
                  <span
                    data-testid="auction-status"
                    className={`font-code text-[11px] uppercase tracking-[0.3em] ${isOpen ? "text-amber-400" : "text-red-400"}`}
                  >
                    ● {isOpen ? a.open : a.closed}
                  </span>
                </div>
                <Countdown endsAt={endsAt} />
                <p data-testid="auction-closes-date" className="mt-3 text-sm text-slate-400">{a.closesDate}</p>
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/subasta"
                  data-testid="auction-cta-register"
                  className="group inline-flex items-center justify-center gap-3 bg-amber-400 px-6 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors hover:bg-amber-300"
                >
                  <Gavel size={16} />
                  {getToken() ? a.ctaEnter : a.ctaRegister}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="space-y-4">
            <Reveal delay={0.2}>
              <div data-testid="auction-leader-card" className="relative overflow-hidden border border-amber-400/50 bg-[#0E1119] p-7">
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-400/10 blur-3xl" />
                <div className="flex items-center gap-3 text-amber-400">
                  <Crown size={18} />
                  <span className="font-code text-[11px] uppercase tracking-[0.3em]">{a.leaderLabel}</span>
                </div>
                <p data-testid="auction-leader-name" className="mt-4 font-display text-2xl font-bold uppercase leading-tight text-white sm:text-3xl">
                  {data?.leader || a.noBids}
                </p>
                <div className="mt-6 grid grid-cols-3 gap-4 border-t border-white/10 pt-5">
                  {[
                    [a.bidsLabel, data?.bid_count ?? 0, "auction-bid-count"],
                    [a.biddersLabel, data?.bidder_count ?? 0, "auction-bidder-count"],
                    [a.termLabel, a.termValue, "auction-term"],
                  ].map(([l, v, id]) => (
                    <div key={id}>
                      <div data-testid={id} className="font-display text-2xl font-bold text-white">{v}</div>
                      <div className="mt-1 font-code text-[10px] uppercase tracking-[0.2em] text-slate-500">{l}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-slate-500">
                  <Lock size={12} className="mt-0.5 shrink-0 text-amber-400" /> {a.rules[1]}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div data-testid="auction-rules" className="border border-white/10 bg-[#0C0F16] p-7">
                <div className="flex items-center gap-3 text-slate-300">
                  <Users size={16} />
                  <span className="font-code text-[11px] uppercase tracking-[0.3em]">{a.rulesTitle}</span>
                </div>
                <ol className="mt-4 space-y-3">
                  {a.rules.map((r, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-400">
                      <span className="font-code text-xs text-amber-400">{String(i + 1).padStart(2, "0")}</span>
                      {r}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="auction-video-block"
            className="mt-16 grid gap-8 border border-white/10 bg-[#0C0F16] p-4 lg:grid-cols-[1.5fr_1fr] lg:p-6"
          >
            <div className="relative overflow-hidden">
              <video
                data-testid="auction-video"
                poster="/assets/naming-rights-mundo-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
                className="aspect-video w-full object-cover"
              >
                <source src="/assets/naming-rights-mundo.mp4" type="video/mp4" />
                <source src="/assets/naming-rights-mundo.webm" type="video/webm" />
              </video>
            </div>
            <div className="flex flex-col justify-center px-2 pb-4 lg:pb-0">
              <span className="font-code text-[11px] uppercase tracking-[0.3em] text-amber-400">{a.videoLabel}</span>
              <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-tight text-white sm:text-3xl">{a.videoTitle}</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">{a.videoNote}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
